// =============================================================================
// app/src/paginas/ContasPagarLote.tsx — os documentos do dia, de uma vez.
//
// Pedido do dono (25/09/2026): "eu lhe envio o pdf com todos os documentos que
// capturei no dia e você os separa em documentos únicos (criação de pdf a
// partir da página do pdf) ... e os insere no sistema".
//
// Ele fotografa os boletos do dia de uma vez no CamScanner e sai UM PDF com 8
// ou 9 folhas. Antes, cadastrar isso era abrir o PDF, achar a folha certa,
// recortar, salvar, repetir. Agora:
//
//   1. escolhe o PDF do dia;
//   2. o app separa as folhas (lib/lote: anda pela estrutura do PDF e descarta
//      logotipo e miniatura pela área) e faz UM PDF de cada uma;
//   3. lê as barras de cada folha no próprio aparelho (lerBoletoArquivo);
//   4. para cada uma, procura a conta: o MESMO código já lançado é certeza; o
//      mesmo título (2ª via) e a conta de valor e vencimento parecidos são
//      palpite, e aparecem como palpite;
//   5. o dono confere a lista e manda anexar.
//
// O QUE ESTA TELA NÃO FAZ: criar conta. Folha sem conta correspondente sai com
// o botão "cadastrar", que leva ao formulário de sempre (o mesmo caminho do
// cadastro por um boleto só), com o PDF daquela página já no bolso. Criar 9
// contas em lote, sem olhar uma a uma, é exatamente onde nasceria a conta
// duplicada que a tela do boleto foi feita para evitar.
//
// NADA É GRAVADO sem o dono tocar em "Anexar". A leitura, a separação e o
// casamento acontecem no aparelho, sem ida à rede e sem custo por página.
// =============================================================================
import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { ContaPagar } from '@core/types';
import Aviso from '../componentes/Aviso';
import { base64DeBytes } from '../lib/anexoPrevia';
import { formatarLinha } from '../lib/codigoBarras';
import { TETO_COMPROVANTE } from '../lib/comprovante';
import {
  lancarLote, removerComprovanteConta, salvarCodigoBarrasConta, salvarComprovanteConta,
} from '../lib/consultas';
import { brl, hojeISO } from '../lib/formato';
import { lerBoletoDoArquivo } from '../lib/lerBoletoArquivo';
import { lerDasDoPdf } from '../lib/lerDasArquivo';
import { preenchimentoSugerido, sugerirDoBoleto } from '../lib/sugestao';
import { nomeDaPagina, paginasDoPdf, pdfDeUmaPagina, type PaginaDoLote } from '../lib/lote';
import { mensagemDeErro } from '../lib/supabase';
import type { PreenchidoPeloBoleto } from './ContasPagarFormularios';

const dataBR = (iso: string) => iso.split('-').reverse().join('/');

// 'paga' entrou em 28/09/2026: a 2ª via de um título JÁ QUITADO. Antes ela
// virava 'palpite' com botão, e anexar gravava o código na conta paga e
// trocava o boleto guardado — a tela de um boleto só sempre trancou nesse caso.
type Situacao = 'casada' | 'palpite' | 'paga' | 'nova' | 'ja-tem' | 'anexada' | 'erro';

interface Folha {
  /** NULA na folha que veio de PDF de TEXTO (a guia do governo): ela não tem
   *  foto dentro, então não há página de imagem nenhuma. */
  pagina: PaginaDoLote | null;
  nome: string;
  /** O PDF que vai para a conta. Na folha de foto é a página remontada; na
   *  guia de texto é o arquivo inteiro, que já é de uma página só. */
  pdf: Uint8Array;
  /** A imagem da folha, para a tela mostrar (data:, que a CSP aceita). Nula na
   *  guia de texto — não há o que mostrar, e um quadrado cinza só roubaria a
   *  largura de que o texto precisa no telefone. */
  previa: string | null;
  /** A descrição lida do TEXTO da guia ("DAS Setembro/2026 — parcela 7/60").
   *  Nula na folha de foto. Sem ela a conta nasceria chamada "órgão do
   *  governo", que é o nome do SEGMENTO do código, não do documento. */
  descricaoDoTexto: string | null;
  situacao: Situacao;
  linha: string | null;
  valor: number | null;
  vencimento: string | null;
  quem: string | null;
  conta: ContaPagar | null;
  /** O que preencher se esta folha virar conta nova, e o que disso é palpite. */
  sugerido: ReturnType<typeof preenchimentoSugerido> | null;
  recado: string | null;
}

export function LoteDoDia({ contas, aoFechar, aoPreencher, aoMudar }: {
  contas: ContaPagar[];
  aoFechar: () => void;
  aoPreencher: (p: PreenchidoPeloBoleto) => void;
  aoMudar: () => void;
}) {
  const entrada = useRef<HTMLInputElement>(null);
  const [folhas, setFolhas] = useState<Folha[] | null>(null);
  // O nome do PDF é como o dono reconhece a leva no feed depois
  // ("CamScanner 28-09-2026 17.14.pdf"). Sem ele o lote é um número.
  const [arquivoDoLote, setArquivoDoLote] = useState<string | null>(null);
  const [lendo, setLendo] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const hoje = hojeISO();

  /** Separa, lê e casa — tudo no aparelho, uma folha de cada vez para a tela
   *  poder dizer em qual está. */
  const abrir = useMutation({
    mutationFn: async (arquivo: File): Promise<Folha[]> => {
      setErro(null);
      setFolhas(null);
      setArquivoDoLote(arquivo.name);
      const bytes = new Uint8Array(await arquivo.arrayBuffer());
      const paginas = paginasDoPdf(bytes);
      if (!paginas.length) {
        // A GUIA DO GOVERNO (28/09/2026). Este é o único ponto em que o
        // caminho novo roda, e é justamente onde o antigo já era beco sem
        // saída: sem foto dentro, o leitor de barras não tinha o que ler. A
        // própria mensagem abaixo dizia "o PDF que o banco gera não tem foto
        // dentro" — ou seja, o arquivo que chegava aqui era exatamente este.
        // O caminho da FOTO não muda uma linha: quando há folha, nem passa
        // por aqui.
        setLendo('Lendo o documento…');
        const das = await lerDasDoPdf(bytes, hoje);
        if (das.ok) {
          const s = sugerirDoBoleto(das.campos.linha, contas, hoje);
          const paga = s.tituloPago ?? (s.mesmoCodigo?.pago_em ? s.mesmoCodigo : null);
          const conta = paga ?? s.mesmoCodigo ?? s.candidatas[0] ?? null;
          setLendo(null);
          return [{
            pagina: null,
            nome: arquivo.name,
            pdf: bytes,
            previa: null,
            descricaoDoTexto: das.campos.descricao || null,
            situacao: paga ? 'paga'
              : conta?.boleto ? 'ja-tem'
                : s.mesmoCodigo ? 'casada' : conta ? 'palpite' : 'nova',
            linha: das.campos.linha,
            // O valor e a data vêm do TEXTO: o código de convênio não carrega
            // data nenhuma, e é por isso que o leitor de barras sozinho nunca
            // daria conta desta guia.
            valor: das.campos.valor,
            vencimento: das.campos.vencimento,
            quem: s.dados?.quem ?? null,
            sugerido: preenchimentoSugerido(s),
            conta,
            recado: das.campos.soNoFormulario
              ? `Cadastre pelo formulário: ${das.campos.soNoFormulario}.`
              : bytes.length > TETO_COMPROVANTE
                ? 'Este documento passou de 2,5 MB e o banco vai recusar.'
                : null,
          }];
        }
        setLendo(null);
        throw new Error(das.motivo
          || 'Não achei nenhuma folha neste PDF. Ele precisa ser a digitalização '
            + '(CamScanner, Notas do iPhone), ou uma guia do governo (DAS, imposto) '
            + 'no PDF original — foto de guia não serve.');
      }
      const fora: Folha[] = [];
      for (const pagina of paginas) {
        setLendo(`Lendo a folha ${pagina.numero} de ${paginas.length}…`);
        // Deixa a tela pintar antes do trabalho pesado de cada folha.
        await new Promise((r) => setTimeout(r, 0));
        const pdf = pdfDeUmaPagina(pagina);
        const nome = nomeDaPagina(pagina.numero, paginas.length);
        const imagem = new File([pagina.jpeg.slice()], `${nome}.jpg`, { type: 'image/jpeg' });
        const leitura = await lerBoletoDoArquivo(imagem);
        const linha = leitura.ok ? leitura.leituras[0]!.linha : null;
        // O MESMO motor da tela de um boleto só (lib/sugestao.ts). Aqui havia
        // uma cópia mutilada dele, e foi ela que carregou os quatro defeitos
        // descritos no cabeçalho daquele arquivo — entre eles passar `hoje`
        // como vencimento de convênio, que casa com qualquer conta da semana.
        const s = linha ? sugerirDoBoleto(linha, contas, hoje) : null;
        const dados = s?.dados ?? null;
        // A PAGA vem primeiro na decisão, e não é candidata: a tela de um
        // boleto só tranca nela ("já foi PAGO em… Não pague de novo") e aqui
        // ela virava palpite com botão, que gravava o código na conta quitada
        // e trocava o boleto guardado.
        const paga = s?.tituloPago ?? (s?.mesmoCodigo?.pago_em ? s.mesmoCodigo : null);
        const conta = paga ?? s?.mesmoCodigo ?? s?.candidatas[0] ?? null;
        fora.push({
          pagina, nome, pdf,
          descricaoDoTexto: null,
          previa: `data:image/jpeg;base64,${base64DeBytes(pagina.jpeg)}`,
          // A conta que JÁ tem boleto não entra no lote: anexar por cima
          // troca o documento guardado, e fazer isso em bloco apagaria o que
          // já estava lá sem ninguém ver. Fica visível, com botão próprio.
          situacao: !linha ? 'nova'
            : paga ? 'paga'
              : conta?.boleto ? 'ja-tem'
                : s?.mesmoCodigo ? 'casada' : conta ? 'palpite' : 'nova',
          linha,
          valor: dados?.valor ?? null,
          vencimento: dados?.vencimento ?? null,
          quem: dados?.quem ?? null,
          sugerido: s ? preenchimentoSugerido(s) : null,
          conta,
          recado: pdf.length > TETO_COMPROVANTE
            ? 'Esta folha passou de 2,5 MB e o banco vai recusar. Digitalize em qualidade menor.'
            : null,
        });
      }
      setLendo(null);
      return fora;
    },
    onSuccess: setFolhas,
    onError: (e) => { setLendo(null); setErro(mensagemDeErro(e)); },
  });

  /** Anexa a folha na conta: o PDF vai para a vaga do BOLETO, e o código de
   *  barras lido vai junto quando a conta ainda não tem um. */
  const anexar = useMutation({
    mutationFn: async (f: Folha) => {
      if (!f.conta) throw new Error('Sem conta escolhida.');
      if (f.linha && !f.conta.codigo_barras) {
        await salvarCodigoBarrasConta({ id: f.conta.id, codigo: f.linha });
      }
      await salvarComprovanteConta({
        contaId: f.conta.id,
        base64: base64DeBytes(f.pdf),
        contentType: 'application/pdf',
        nome: f.nome,
        tipo: 'boleto',
      });
      return f.nome;
    },
    onSuccess: (nome) => {
      setFolhas((atual) => (atual ?? []).map((f) => (f.nome === nome
        ? { ...f, situacao: 'anexada' as const, recado: null } : f)));
      aoMudar();
    },
    onError: (e, f) => {
      const recado = mensagemDeErro(e);
      setFolhas((atual) => (atual ?? []).map((x) => (x.nome === f.nome
        ? { ...x, situacao: 'erro' as const, recado } : x)));
    },
  });

  /** DESFAZER a anexação, sem sair da tela. O dono relatou em 28/09/2026 que
   *  "não tem como remover" um comprovante — a porta existia, mas só na lista,
   *  dentro do detalhe da conta. Quem acabou de anexar nove folhas de um PDF
   *  precisa poder desfazer AQUI, olhando a folha que anexou. */
  const desanexar = useMutation({
    mutationFn: async (f: Folha) => {
      if (!f.conta) throw new Error('Sem conta.');
      await removerComprovanteConta(f.conta.id, 'boleto');
      return f;
    },
    onSuccess: (f) => {
      setFolhas((atual) => (atual ?? []).map((x) => (x.nome === f.nome
        ? { ...x, situacao: x.conta?.pago_em ? 'paga' : 'casada', recado: null } : x)));
      aoMudar();
    },
    onError: (e, f) => {
      setFolhas((atual) => (atual ?? []).map((x) => (x.nome === f.nome
        ? { ...x, recado: mensagemDeErro(e) } : x)));
    },
  });

  /** CADASTRAR TODAS DE UMA VEZ (086). Só as folhas 'nova': as que casaram com
   *  conta existente se anexam, não se cadastram.
   *
   *  A porta é o `lancar_lote`, que é ATÔMICO — ou entram todas, ou não entra
   *  nenhuma. Meio lote criado deixaria conta duplicada na segunda tentativa,
   *  e a segunda tentativa é certa: o dono vai tentar de novo. */
  const cadastrarTodas = useMutation({
    mutationFn: async (lista: Folha[]) => {
      const nome = arquivoDoLote ?? 'PDF do dia';
      return lancarLote(nome, total, lista.map((f) => ({
        descricao: f.sugerido?.descricao || f.descricaoDoTexto || f.quem || 'Conta do boleto',
        categoria: f.sugerido?.categoria ?? 'outros',
        valor: f.valor,
        vencimento: f.vencimento ?? hoje,
        fornecedor_id: f.sugerido?.fornecedorId ?? null,
        codigo: f.linha,
      })));
    },
    onSuccess: () => { setFolhas(null); aoMudar(); aoFechar(); },
  });

  const anexarTodas = useMutation({
    mutationFn: async (lista: Folha[]) => {
      for (const f of lista) await anexar.mutateAsync(f);
    },
  });

  // O bloco só toca o que o motor libera: código batendo exatamente, conta não
  // paga e sem boleto. Palpite se olha um a um; conta paga não entra nunca.
  const prontas = (folhas ?? []).filter((f) => f.situacao === 'casada' && !f.recado);
  // As que viram conta NOVA. Só entram aqui depois que o motor de sugestão
  // passou a existir (etapa F): antes, a descrição sugerida era o nome do
  // BANCO, e cadastrar nove de uma vez criaria "Bradesco", "Itaú", "Sicredi".
  const novas = (folhas ?? []).filter((f) => f.situacao === 'nova' && f.linha && f.vencimento);
  const total = folhas?.length ?? 0;

  return (
    <div className="cartao space-y-3 p-4">
      <div className="flex items-baseline justify-between">
        <h2 className="rotulo">Documentos do dia</h2>
        <button type="button" className="text-xs text-dim underline" onClick={aoFechar}>fechar</button>
      </div>

      <p className="text-xs text-mute">
        Escolha o PDF com tudo o que você fotografou hoje. Eu separo folha por folha,
        faço um PDF de cada uma, leio o código de barras no próprio aparelho e procuro
        a conta. <strong>Nada é gravado até você mandar.</strong>
      </p>

      <input ref={entrada} type="file" accept="application/pdf,.pdf" className="hidden"
             onChange={(e) => {
               const f = e.target.files?.[0];
               e.target.value = '';
               if (f) abrir.mutate(f);
             }} />
      <button type="button" className="botao-primario w-full py-2 text-sm"
              disabled={abrir.isPending}
              onClick={() => entrada.current?.click()}>
        {abrir.isPending ? (lendo ?? 'Separando as folhas…') : 'Escolher o PDF do dia'}
      </button>

      {erro && <Aviso tipo="erro">{erro}</Aviso>}

      {folhas && (
        <>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm">
              {/* As contagens levam o sinal no NÚMERO (Grafite); a palavra fica
                  no tom do texto. */}
              <strong className="numero font-medium">{total}</strong> folha{total > 1 ? 's' : ''} ·{' '}
              <span><span className="numero text-ok">{folhas.filter((f) => f.situacao === 'casada').length}</span> com conta certa</span> ·{' '}
              <span><span className="numero text-alerta">{folhas.filter((f) => f.situacao === 'palpite').length}</span> para conferir</span> ·{' '}
              <span className="text-dim"><span className="numero">{folhas.filter((f) => f.situacao === 'nova').length}</span> sem conta</span>
              {folhas.some((f) => f.situacao === 'paga') && (
                <> · <span>
                  <span className="numero text-erro">{folhas.filter((f) => f.situacao === 'paga').length}</span> já paga(s)
                </span></>
              )}
              {folhas.some((f) => f.situacao === 'ja-tem') && (
                <> · <span className="text-dim">
                  {folhas.filter((f) => f.situacao === 'ja-tem').length} já tinha boleto
                </span></>
              )}
            </p>
            <div className="flex flex-wrap gap-2">
              {prontas.length > 0 && (
                <button type="button" className="botao-primario px-3 py-1.5 text-xs"
                        disabled={anexarTodas.isPending || cadastrarTodas.isPending}
                        onClick={() => anexarTodas.mutate(prontas)}>
                  {anexarTodas.isPending
                    ? 'Anexando…'
                    : `Anexar as ${prontas.length} certas`}
                </button>
              )}
              {novas.length > 0 && (
                <button type="button" className="botao-secundario px-3 py-1.5 text-xs"
                        disabled={anexarTodas.isPending || cadastrarTodas.isPending}
                        onClick={() => cadastrarTodas.mutate(novas)}>
                  {cadastrarTodas.isPending
                    ? 'Cadastrando…'
                    : `Cadastrar as ${novas.length} sem conta`}
                </button>
              )}
            </div>
          </div>

          <ul className="space-y-2">
            {folhas.map((f) => (
              <li key={f.nome} className="border border-edge bg-panel-alt p-2">
                <div className="flex gap-3">
                  {f.previa && (
                    <img src={f.previa} alt={`Folha ${f.pagina?.numero ?? 1}`}
                         className="h-24 w-24 shrink-0 border border-edge bg-paper object-contain" />
                  )}
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <p className="text-xs text-dim">{f.nome} · {Math.round(f.pdf.length / 1024)} KB</p>
                    {f.linha ? (
                      <>
                        <p className="text-sm">
                          {f.quem}
                          {f.valor !== null && <> · <span className="numero">{brl(f.valor)}</span></>}
                          {f.vencimento && <> · vence {dataBR(f.vencimento)}</>}
                        </p>
                        <p className="break-all font-mono text-[0.625rem] text-dim">
                          {formatarLinha(f.linha)}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-mute">Sem código de barras legível nesta folha.</p>
                    )}

                    {f.situacao === 'anexada' && (
                      <p className="text-xs text-mute">
                        <span className="selo selo-pago mr-1.5">anexada</span>
                        Anexada em {f.conta?.descricao}.{' '}
                        <button type="button" className="text-dim underline hover:text-paper"
                                disabled={desanexar.isPending}
                                onClick={() => desanexar.mutate(f)}>
                          tirar o boleto desta conta
                        </button>
                      </p>
                    )}
                    {f.situacao === 'casada' && f.conta && (
                      <p className="text-xs text-mute">
                        <span className="selo selo-pago mr-1.5">conta certa</span>
                        É a conta <strong className="text-paper">{f.conta.descricao}</strong>, de {dataBR(f.conta.vencimento)} —
                        o código de barras é o mesmo.
                      </p>
                    )}
                    {f.situacao === 'palpite' && f.conta && (
                      <p className="text-xs text-mute">
                        <span className="selo selo-hoje mr-1.5">conferir</span>
                        Parece a conta <strong className="text-paper">{f.conta.descricao}</strong>, de {dataBR(f.conta.vencimento)}.
                        Confira antes.
                      </p>
                    )}
                    {f.situacao === 'paga' && f.conta && (
                      <p className="text-xs text-paper">
                        <span className="selo selo-atrasado mr-1.5">já paga</span>
                        A conta <strong>{f.conta.descricao}</strong> já foi <strong>PAGA</strong>
                        {f.conta.pago_em ? ` em ${dataBR(f.conta.pago_em)}` : ''}. Isto é 2ª via —
                        não pague de novo, e não anexe por cima do comprovante.
                      </p>
                    )}
                    {f.situacao === 'nova' && (
                      <p className="text-xs text-dim">Nenhuma conta parecida — esta folha vira conta nova.</p>
                    )}
                    {f.situacao === 'ja-tem' && f.conta && (
                      <p className="text-xs text-dim">
                        A conta <strong>{f.conta.descricao}</strong> já tem um boleto guardado.
                        Fora do lote — anexe só se for para trocar o documento.
                      </p>
                    )}
                    {f.recado && <p className="text-xs text-erro">{f.recado}</p>}

                    <div className="flex flex-wrap gap-x-3 gap-y-1 pt-1 text-xs">
                      {/* 'paga' fica de fora: anexar nela grava o código na conta
                          quitada e troca o comprovante guardado. É o caso em que
                          a tela de um boleto só tranca, e aqui oferecia botão. */}
                      {f.situacao !== 'nova' && f.situacao !== 'anexada' && f.situacao !== 'paga' && f.conta && (
                        <button type="button" className="inline-flex min-h-11 items-center text-signal-soft underline hover:text-paper sm:min-h-0"
                                disabled={anexar.isPending || anexarTodas.isPending}
                                onClick={() => anexar.mutate(f)}>
                          {f.situacao === 'ja-tem' ? 'trocar o boleto desta conta' : 'anexar nesta conta'}
                        </button>
                      )}
                      {f.situacao === 'nova' && (
                        <button type="button" className="inline-flex min-h-11 items-center text-signal-soft underline hover:text-paper sm:min-h-0"
                                onClick={() => aoPreencher({
                                  // Era `f.quem` — o nome do BANCO. Cadastrar
                                  // em bloco criaria contas "Bradesco",
                                  // "Itaú", "Sicredi". Agora vem do motor, que
                                  // só sugere descrição quando existe boleto
                                  // anterior da mesma cobrança.
                                  // Sem código legível não há o que sugerir: vai
                                  // vazio, e o formulário cai no padrão dele
                                  // ('outros'). Antes ia 'fornecedores', que é
                                  // chute — a maioria dos boletos é, mas a folha
                                  // que não se leu pode ser qualquer coisa.
                                  ...(f.sugerido ?? {
                                    descricao: '', categoria: 'outros' as const,
                                    fornecedorId: null, palpites: [], porque: null,
                                  }),
                                  valor: f.valor,
                                  vencimento: f.vencimento,
                                  codigo: f.linha ?? '',
                                  arquivo: new File([f.pdf.slice()], f.nome, { type: 'application/pdf' }),
                                })}>
                          cadastrar a conta
                        </button>
                      )}
                      <a className="text-dim underline"
                         href={`data:application/pdf;base64,${base64DeBytes(f.pdf)}`}
                         download={f.nome}>baixar só esta</a>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
